import {
	useEffect,
	useMemo,
	useState,
	useCallback,
	PropsWithChildren
} from 'react';
import {
	GoogleMap,
	PolygonF,
	MarkerF,
	MarkerProps,
	PolygonProps
} from '@react-google-maps/api';
import { GoogleMapsLoaded } from '@state';
import { useRecoilValue } from 'recoil';
import { Loader } from '@components';
import styles from './GMap.module.scss';
import { GeoJsonLayer } from './GeoJSON';
import { AU_BOUND } from '@types';

type MapBounds = google.maps.LatLngBounds | google.maps.LatLngBoundsLiteral;

export interface GeojsonMapComponentProps {
    mapContainerClassName?: string;
	mapContainerStyle?: Record<string, string>,
    options?: google.maps.MapOptions;
    bounds?: MapBounds | null;
	width?: string;
	height?: string;
	multiPolygonArray?: PolygonProps[];
	multimarker?: MarkerProps[];
	loadingData?: boolean;
}

export const GMapCore = (props: PropsWithChildren<GeojsonMapComponentProps>) => {
	const isLoaded = useRecoilValue(GoogleMapsLoaded);
	const [map, setMap] = useState<google.maps.Map | null>(null);
	const {
		mapContainerClassName = '',
		mapContainerStyle = { width: '100%', height: '100%' },
		options = {},
		bounds = null,
		width = '100%',
		height = '350px',
		multiPolygonArray,
		multimarker,
		children,
		loadingData
	} = props;

	const mergedOptions = useMemo(() => ({
		minZoom: 4,
		maxZoom: 16,
		scrollwheel: false,
		mapTypeControl: false,
		fullscreenControl: true,
		streetViewControl: false,
		...options,
	}), [options]);

	const onLoad = useCallback((map: google.maps.Map) => {
		setMap(map);
	}, []);

	const onUnmount = useCallback(() => {
		setMap(null);
	}, []);

	useEffect(() => {
		const timeout = setTimeout(() => {
			map && map.fitBounds(bounds || AU_BOUND, {
				bottom: 5,
				left: 5,
				right: 5,
				top: 5,
			});
		}, 200);

		return () => clearTimeout(timeout);
	}, [bounds, map]);

	return (
		<>
			{isLoaded ?
				<div style={{ width, height }} className={styles.container}>
					{loadingData ? <Loader /> :
						<GoogleMap
							mapContainerClassName={mapContainerClassName}
							options={mergedOptions}
							onLoad={onLoad}
							onUnmount={onUnmount}
							mapContainerStyle={mapContainerStyle}
							center={undefined}
							zoom={3}
						>
							{multiPolygonArray?.map((element, index) => {
								let props = element;
								if (Array.isArray(element)) {
									props = {
										paths: element,
										options: {
											strokeColor: '#F05637',
											fillColor: '#F05637',
											fillOpacity: 0.5,
											strokeOpacity: 0.5,
											strokeWeight: 2,
										},
									};
								}
								return <PolygonF key={index} {...props} />;
							})}
							{multimarker?.map((marker, i) => {
								return <MarkerF key={i} {...marker} />;
							})}
							{children}
						</GoogleMap>
					}
				</div> :
				null
			}
		</>
	);
}

const PolygonExt = (props: PolygonProps) => {
	return (
		<PolygonF options={{
			strokeColor: '#F05637',
			fillColor: '#F05637',
			fillOpacity: 0.5,
			strokeOpacity: 0.5,
			strokeWeight: 2,
		}} {...props} />
	);
}

GMapCore.GeoJson = GeoJsonLayer;
GMapCore.Polygon = PolygonExt;
export const GMap = GMapCore;
